<template>
    <div class="container">
        <v-title
            :lineage="lineage">
            {{ pageTitle }}
            <div slot="actions">
                <r-button
                    v-if="showRestoreButton"
                    type="primary"
                    class="inline-s"
                    :disabled="isLoading"
                    @click="handleRestoreVersion">Restore version {{ activeVersion }}
                </r-button>
            </div>
        </v-title>
        <div class="grid">
            <div class="grid-item grid-item-secondary">
                <div class="tile">
                    <div class="tile-content position-relative">
                        <versions-list-filter
                            v-if="currentVersion"
                            :version="activeVersion"
                            :filter-params="ruleHistoryFilter"
                            :items="ruleHistory"
                            :is-loading="isLoading"
                            date-filter-key-name="createdTime"
                            empty-message="No rules exist at this date"
                            @filter="handleUpdateHistoryFilter"
                            @navigate="handleNavigateToVersion"/>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-primary">
                <div class="tile">
                    <div>
                        <v-inline-loader v-if="isLoading"/>
                        <r-tabs :divided="true" v-if="ruleSet">
                            <r-tab
                                :name="getTabLabel('rules')"
                                :to="{name: $route.name, params: {eventType, view: 'rules', version: $route.params.version}}">
                                <table class="table table-compact table-hoverable">
                                    <colgroup>
                                        <col style="width:100px;">
                                        <col>
                                    </colgroup>
                                    <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Rules</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="rule in ruleSet.rules" :key="rule.order">
                                        <td>
                                            <r-badge type="positive" v-if="rule.status">Active</r-badge>
                                            <r-badge type="negative" v-else>Inactive</r-badge>
                                        </td>
                                        <td @click="toggleRuleDetails(rule)" class="cursor-pointer">
                                            <div v-if="isDetailsExpanded(rule)" class="stack-m"></div>
                                            <span class="inline-xxs">{{rule.name}}</span>
                                            <v-icon :icon="ruleCaretIcon(rule)" class="inline-s"/>
                                            <span v-if="rule.final" class="float-right">
                                                <v-icon icon="stop" class="inline-s"/><span class="text-muted">Stops further rule processing</span>
                                            </span>
                                            <div v-if="isDetailsExpanded(rule)" class="stack-m"></div>
                                            <rule-summary
                                                v-if="isDetailsExpanded(rule)"
                                                :event="systemEvent"
                                                :rule="rule"/>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </r-tab>
                            <r-tab
                                :name="getTabLabel('binds')"
                                :to="{name: $route.name, params: {eventType, view: 'binds', version: $route.params.version}}">
                                <table class="table table-compact table-hoverable">
                                    <colgroup>
                                        <col style="width:100px;">
                                        <col>
                                    </colgroup>
                                    <thead>
                                    <tr>
                                        <th>Status</th>
                                        <th>Binds</th>
                                    </tr>
                                    </thead>
                                    <tbody>

                                    <tr v-for="rule in ruleSet.binds" :key="rule.name">
                                        <td>
                                            <r-badge type="positive" v-if="rule.status">Active</r-badge>
                                            <r-badge type="negative" v-else>Inactive</r-badge>
                                        </td>
                                        <td @click="toggleRuleDetails(rule)" class="cursor-pointer">
                                            <div v-if="isDetailsExpanded(rule)" class="stack-m"></div>
                                            <span class="inline-xxs">{{rule.name}}</span>
                                            <v-icon :icon="ruleCaretIcon(rule)" class="inline-s"/>
                                            <div v-if="isDetailsExpanded(rule)" class="stack-m"></div>
                                            <rule-summary
                                                v-if="isDetailsExpanded(rule)"
                                                :event="systemEvent"
                                                :rule="rule"/>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </r-tab>
                        </r-tabs>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import _ from '@/common/helpers';
    import {createDialog, vConfirm} from '@/components/modal/dialogs/index';
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import RuleSetModel from '@/common/models/rules/rule-set-model';
    import vTitle from '@/components/v-title';
    import {vIcon} from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import EventModel from '@/common/models/rules/event-model';
    import RuleSet from './components/ruleset/rule-set';
    import RuleSummary from './actions/rule-summary';
    import VersionsListFilter from './components/common/versions-list-filter';

    const defaultRuleHistoryFilter = {
        limit: 5,
        offset: 0,
        filter: {},
    };

    export default {
        name: 'event-history',
        mixins: [LoadStateMixin],
        components: {
            vTitle,
            vIcon,
            RuleSet,
            VersionsListFilter,
            RuleSummary,
            vInlineLoader,
        },
        props: {
            eventType: String,
            eventId: String,
            version: [Number, String],
        },
        computed: {
            activeVersion() {
                return +this.version;
            },
            showRestoreButton() {
                return this.activeVersion !== this.currentVersion;
            },
            eventTitle() {
                return _.unKebabCase(this.eventType);
            },
            systemEvent() {
                return new EventModel({eventType: this.eventType});
            },
            pageTitle() {
                return `${this.eventTitle} - Version ${this.activeVersion} - Rule Set History`;
            },
            lineage() {
                return [
                    {
                        [this.eventTitle]: {
                            name: 'view-system-event',
                            params: {
                                eventType: this.eventType,
                            },
                        },
                    },
                ];
            },
        },
        data() {
            return {
                currentVersion: null,
                ruleSet: null,
                ruleHistory: [],
                expandedRules: [],
                isLoading: false,
                ruleHistoryFilter: {
                    ...defaultRuleHistoryFilter,
                    eventType: this.eventType,
                },
            };
        },
        methods: {
            async handleRestoreVersion() {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Restore rule',
                    content: `Are you sure you want to restore your current event to this rule set? The change will be applied immediately.`,
                    confirmLabel: 'Yes',
                };

                const isConfirmed = await confirmDialog(dialogParams);

                if (!isConfirmed) {
                    return;
                }

                this.isLoading = true;
                const {fields} = await this.getDelegate(
                    (type, entityId, entityKey) => type.updateRules({
                        data: this.ruleSet.getPayload(),
                        [entityKey]: entityId,
                    }),
                );

                this.$router.push({
                    ...this.$route,
                    params: {
                        ...this.$route.params,
                        version: fields.version,
                    },
                });

                this.$nextTick(() => {
                    this.isLoading = false;
                });
            },
            handleUpdateHistoryFilter(filter = defaultRuleHistoryFilter) {
                this.ruleHistoryFilter = {
                    ...defaultRuleHistoryFilter,
                    ...filter,
                    filter: {
                        ...filter.filter,
                        eventType: this.eventType,
                    },
                };
                this.ruleHistory = [];
                this.$nextTick(() => {
                    this.fetchData();
                });
            },
            handleNavigateToVersion({version}) {
                this.$router.push({
                    name: 'view-system-event-history',
                    params: {
                        eventType: this.eventType,
                        version,
                    },
                });
            },
            /**
             * Return the correct API namespace based
             * on the type of event we're viewing.
             * @param callback
             * @returns {promise} - callback.apply(this, [api, entityId, entityIdKey]
             * entityId - eventType or eventId valu
             * entityIdKey - 'eventType' or 'eventId' string name
             */
            getDelegate(callback) {
                return this.$api(({events}) => callback.apply(this, [events, this.eventType, 'eventType']));
            },
            async fetchData() {
                this.isLoading = true;
                try {
                    const {
                        eventType, ruleHistoryFilter, ruleHistory, currentVersion,
                    } = this;

                    const currentRuleIsLoaded = !!currentVersion;
                    const historyIsLoaded = !!ruleHistory
                        .find(item => item.version === this.activeVersion);

                    // returns current (latest) version
                    const getCurrentRule = !currentRuleIsLoaded
                        ? this.getDelegate(type => type.getRulesHistory({
                            limit: 1,
                            eventType,
                        }))
                        : Promise.resolve({items: [{fields: {version: currentVersion}}]});

                    // returns versions list
                    const getRuleSetHistory = !historyIsLoaded
                        ? this.getDelegate(type => type.getRulesHistory({
                            ...ruleHistoryFilter,
                            eventType,
                        }))
                        : Promise.resolve({items: []});

                    // returns rules for opened version
                    const getActiveRuleSet = this.getDelegate(type => type.getRulesVersionDetail({
                        version: this.activeVersion,
                        eventType,
                    }));

                    const [
                        {items: [{fields: {version: currentRuleSetVersion}}]},
                        {items: ruleSetHistoryItems},
                        activeRuleSet,
                    ] = await Promise.all([getCurrentRule, getRuleSetHistory, getActiveRuleSet]);

                    if (!currentRuleIsLoaded) {
                        this.currentVersion = currentRuleSetVersion;
                    }
                    if (!historyIsLoaded) {
                        this.ruleHistory = ruleSetHistoryItems
                            .map(item => new RuleSetModel(item.fields));
                    }

                    this.ruleSet = new RuleSetModel(activeRuleSet.fields);
                    this.isLoading = false;
                } catch (e) {
                    // goto previous route
                    this.$toast.negative(`History loading error.`);
                    this.isLoading = false;
                    this.$redirectOnError(e, Object.values(this.lineage[0])[0]);
                }
            },
            getTabLabel(type) {
                let particle = '';
                if (this.ruleSet[`${type}Count`]) {
                    const count = this.ruleSet[`${type}Count`];
                    particle = ` (${count})`;
                }
                return `${_.capitalize(type)}${particle}`;
            },
            ruleCaretIcon(rule) {
                return this.isDetailsExpanded(rule)
                    ? 'caret-down'
                    : 'caret-right';
            },
            isDetailsExpanded(rule) {
                return this.expandedRules.includes(rule.order);
            },
            toggleRuleDetails(rule) {
                if (this.expandedRules.includes(rule.order)) {
                    this.expandedRules.splice(this.expandedRules.indexOf(rule.order), 1);
                } else {
                    this.expandedRules.push(rule.order);
                }
            },
        },
        watch: {
            $route(route) {
                this.version = route.params.version;
                this.expandedRules = [];
                this.ruleSet = null;
                this.fetchData();
            },
        },
    };
</script>
